import {
  useEffect,
  useLayoutEffect,
  useRef,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
  type RefObject
} from 'react'

import { rowClass, slugifyGroup } from './console-nav-row.tsx'
import { Icon, type IconName } from '../design-system/icon.tsx'
import { Link } from '../design-system/link.tsx'
import { cn } from '../lib/cn.ts'
import {
  normalizePath,
  type InternalNavSection,
  type InternalPage
} from '../lib/pages.ts'

/** The reports in one category: title, description, and a star. */
function CategoryReport({
  page,
  active,
  testId,
  onNavigate,
  icon,
  starsEnabled,
  starred,
  onToggleStar
}: {
  page: InternalPage
  active: boolean
  testId: string
  onNavigate: () => void
  icon?: IconName
  starsEnabled: boolean
  starred: boolean
  onToggleStar: () => void
}): JSX.Element {
  const titleId = `${testId}-title`
  const descriptionId = `${testId}-description`

  return (
    <div data-report-row="" className="flex items-start">
      <Link
        data-testid={testId}
        href={page.path}
        onNavigate={onNavigate}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'text-sidebar-foreground flex min-w-0 flex-1 flex-col items-start gap-1 px-3 py-2 text-left text-sm',
          'hover:bg-sidebar-accent cursor-pointer',
          active &&
            'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
        )}
      >
        {icon ? <Icon name={icon} className="size-4 shrink-0" /> : null}
        <span id={titleId}>{page.title}</span>
        <span
          id={descriptionId}
          data-testid={descriptionId}
          className="text-sidebar-foreground text-xs"
        >
          {page.description}
        </span>
      </Link>
      {starsEnabled ? (
        <button
          type="button"
          data-testid={`${testId}-star`}
          aria-pressed={starred}
          aria-label={`Star ${page.title}`}
          className={cn(
            'text-sidebar-foreground hover:bg-sidebar-accent mt-1 shrink-0 cursor-pointer p-2',
            starred && 'text-sidebar-accent-foreground'
          )}
          onClick={() => onToggleStar()}
        >
          <Icon
            name="star"
            className={cn('size-4', starred && 'fill-current')}
          />
        </button>
      ) : null}
    </div>
  )
}

function reportAnchor(
  panel: HTMLElement,
  index: number
): HTMLAnchorElement | null {
  const links = panel.querySelectorAll<HTMLAnchorElement>('a')
  if (links.length === 0) return null
  const next = index < 0 ? links.length - 1 : Math.min(index, links.length - 1)
  return links[next] ?? null
}

/** Move focus between the report links in a category panel. */
function onReportKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
  const links = [
    ...event.currentTarget.querySelectorAll<HTMLAnchorElement>('a')
  ]
  if (links.length === 0) return

  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
  const active = document.activeElement
  let index = -1
  if (active instanceof HTMLAnchorElement) {
    index = links.indexOf(active)
  } else if (active instanceof HTMLElement) {
    const row = active.closest('[data-report-row]')
    index = links.findIndex(link => row?.contains(link))
  }
  event.preventDefault()
  const next =
    event.key === 'ArrowDown'
      ? index < 0
        ? 0
        : Math.min(index + 1, links.length - 1)
      : index < 0
        ? links.length - 1
        : Math.max(index - 1, 0)
  links[next]?.focus()
}

/** Category rows, their desktop panel, and the mobile report list. */
export function CategoryPanel({
  sections,
  openGroup,
  mobile,
  activePath,
  onToggleGroup,
  onCloseGroup,
  onNavigate,
  starsEnabled,
  starredIds,
  onToggleStar,
  navRef,
  sheetOpen,
  children
}: {
  sections: readonly InternalNavSection[]
  openGroup: string | null
  mobile: boolean
  activePath: string
  onToggleGroup: (group: string) => void
  onCloseGroup: () => void
  onNavigate: (path: string) => void
  starsEnabled: boolean
  starredIds: ReadonlySet<string>
  onToggleStar: (id: string) => void
  navRef: RefObject<HTMLElement | null>
  sheetOpen: boolean
  children: ReactNode
}): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null)
  const groupButtons = useRef(new Map<string, HTMLButtonElement>())
  const backRef = useRef<HTMLButtonElement>(null)
  const previousMobileGroup = useRef<string | null>(null)
  const prefix = mobile ? 'mobile-' : ''
  const openSection =
    sections.find(section => section.group === openGroup) ?? null
  const panelSection = !mobile ? openSection : null
  const panelGroup = panelSection?.group ?? null
  const drillSection = mobile ? openSection : null
  const drillGroup = drillSection?.group ?? null

  useLayoutEffect(() => {
    if (drillGroup !== null) backRef.current?.focus()
    else if (previousMobileGroup.current !== null) {
      groupButtons.current.get(previousMobileGroup.current)?.focus()
    }
    previousMobileGroup.current = drillGroup
  }, [drillGroup])

  // The panel is portaled nowhere, so a click on the page is outside this
  // nav. A click inside the mobile sheet is not: that sheet is the other
  // nav, and its Back control must not fight this listener.
  useEffect(() => {
    if (panelGroup === null) return () => undefined
    const group = panelGroup
    function onFocusOut(event: FocusEvent): void {
      const target = event.relatedTarget
      if (target instanceof Node && navRef.current?.contains(target)) return
      if (
        target instanceof Element &&
        target.closest(
          '[data-testid="console-nav"], [data-testid="console-nav-toggle"]'
        )
      )
        return
      if (
        event.target instanceof Node &&
        navRef.current?.contains(event.target)
      ) {
        onCloseGroup()
      }
    }
    function onPointerDown(event: PointerEvent): void {
      const target = event.target
      if (!(target instanceof Node)) return
      if (navRef.current?.contains(target)) return
      // The menu button opens the other nav. It sits outside this one, and
      // closing here would drop the category before the sheet can show it.
      if (
        target instanceof Element &&
        target.closest(
          '[data-testid="console-nav"], [data-testid="console-nav-toggle"]'
        )
      ) {
        return
      }
      onCloseGroup()
    }
    function onEscape(event: globalThis.KeyboardEvent): void {
      if (event.key !== 'Escape' || sheetOpen || event.defaultPrevented) return
      if (!navRef.current?.contains(document.activeElement)) return
      event.preventDefault()
      const button = groupButtons.current.get(group)
      onCloseGroup()
      button?.focus()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onEscape)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onEscape)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [panelGroup, sheetOpen, onCloseGroup, navRef])

  function reports(pages: readonly InternalPage[]): JSX.Element {
    return (
      <>
        {pages.map(page => (
          <CategoryReport
            key={page.id}
            page={page}
            icon={page.icon}
            active={activePath === normalizePath(page.path)}
            testId={`${prefix}nav-${page.id}`}
            onNavigate={() => onNavigate(page.path)}
            starsEnabled={starsEnabled}
            starred={starredIds.has(page.id)}
            onToggleStar={() => onToggleStar(page.id)}
          />
        ))}
      </>
    )
  }

  return (
    <>
      {drillSection ? (
        <div
          data-testid={`${prefix}nav-category-reports`}
          className="flex flex-col gap-1"
          onKeyDown={onReportKeyDown}
        >
          <button
            type="button"
            data-testid={`${prefix}nav-category-back`}
            ref={backRef}
            aria-label="Back to categories"
            className={rowClass(false)}
            onClick={() => onToggleGroup(drillSection.group)}
          >
            <Icon name="chevron-left" className="size-4 shrink-0" />
            Back
          </button>
          <p className="text-sidebar-foreground px-3 py-2 text-xs font-semibold">
            {drillSection.group}
          </p>
          {reports(drillSection.pages)}
        </div>
      ) : null}

      {drillSection === null ? (
        <>
          {children}
          {sections.map(section => {
            const slug = slugifyGroup(section.group)
            const open = openGroup === section.group
            // The current report is not a row in the rail, so the category
            // carries the mark whenever it holds that page.
            const holdsCurrent = section.pages.some(
              page => normalizePath(page.path) === activePath
            )
            const labelParts = [
              section.group,
              holdsCurrent ? 'holds the current page' : null
            ].filter(part => part !== null)

            return (
              <div
                key={section.group}
                data-testid={`${prefix}nav-section-container-${slug}`}
                className="flex flex-col gap-1"
              >
                <button
                  type="button"
                  ref={node => {
                    if (node) groupButtons.current.set(section.group, node)
                    else groupButtons.current.delete(section.group)
                  }}
                  data-testid={`${prefix}nav-group-${slug}`}
                  aria-expanded={open}
                  aria-controls={
                    open && !mobile ? 'nav-category-panel' : undefined
                  }
                  // A visually hidden span beside the dot is the obvious spelling,
                  // but the accessible name trims each node and joins them without
                  // a separator: "BlytzCollectholds the current page".
                  aria-label={
                    labelParts.length > 1 ? labelParts.join(', ') : undefined
                  }
                  className={cn(rowClass(holdsCurrent), 'justify-between')}
                  onClick={() => onToggleGroup(section.group)}
                  onKeyDown={event => {
                    if (!open || mobile) return
                    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
                      return
                    }
                    const panel = panelRef.current
                    if (panel === null) return
                    event.preventDefault()
                    reportAnchor(
                      panel,
                      event.key === 'ArrowDown' ? 0 : -1
                    )?.focus()
                  }}
                >
                  <span className="flex items-center gap-2">
                    {section.group}
                    {holdsCurrent ? (
                      <span
                        aria-hidden="true"
                        className="bg-sidebar-accent-foreground size-1.5 shrink-0"
                      />
                    ) : null}
                  </span>
                  <Icon
                    name={open ? 'chevron-up' : 'chevron-down'}
                    className="size-4 shrink-0"
                  />
                </button>
                {panelSection?.group === section.group ? (
                  <div
                    ref={panelRef}
                    id="nav-category-panel"
                    data-testid="nav-category-panel"
                    role="region"
                    aria-label={panelSection.group}
                    onKeyDown={onReportKeyDown}
                    className="scrollbar-sidebar border-sidebar-border bg-sidebar absolute inset-y-0 left-full z-10 flex w-80 flex-col gap-1 overflow-y-auto border-l border-(length:--rule-width) p-3"
                  >
                    <p className="text-sidebar-foreground px-3 py-2 text-xs font-semibold">
                      {panelSection.group}
                    </p>
                    {reports(panelSection.pages)}
                  </div>
                ) : null}
              </div>
            )
          })}
        </>
      ) : null}
    </>
  )
}
